import React, { useState, useEffect, useMemo } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Button,
  Tooltip,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import "./TypeFilterDropdown.scss";

const TypeFilterDropdown = ({
  typeFilterList = {},
  value = [],
  onChange,
  placeholder = "select",
  disabled = false,
  variant = "type", // ("type" | "extension")
}) => {
  const [tempValue, setTempValue] = useState(value);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setTempValue(value);
    }
  }, [value, open]);

  const options = useMemo(
    () => Object.keys(typeFilterList || {}),
    [typeFilterList],
  );

  const toggleItem = (prev, item) =>
    prev.includes(item) ? prev.filter((v) => v !== item) : [...prev, item];

  // VARIANT-BASED MAX
  const MAX_VISIBLE = variant === "extension" ? 2 : 3;

  const handleToggle = (item) => {
    setTempValue((prev) => {
      const updated = toggleItem(prev, item);
      onChange(updated); // send actual array, not function
      return updated;
    });
  };

  const handleClear = () => {
    setTempValue([]);
    onChange([]);
    setOpen(false);
  };

  const visibleItems = tempValue.slice(0, MAX_VISIBLE);
  const hiddenItems = tempValue.slice(MAX_VISIBLE);

  return (
    <FormControl size="small" className="type-filter">
      <Select
        multiple
        displayEmpty
        disabled={disabled}
        open={open}
        value={tempValue}
        onOpen={() => {
          setTempValue(value);
          setOpen(true);
        }}
        onClose={() => setOpen(false)}
        input={<OutlinedInput />}
        MenuProps={{
          PaperProps: {
            className: "type-filter__menu",
          },
        }}
        renderValue={(selected) =>
          selected.length === 0 ? (
            <span className="type-filter__placeholder">
              {disabled ? "select type first" : placeholder}
            </span>
          ) : variant === "type" && selected.length === 1 ? (
            // TYPE → single → full name
            <Box className="type-filter__selected">
              <Tooltip title={selected[0]}>
                <Box className="type-filter__pill">{selected[0]}</Box>
              </Tooltip>
            </Box>
          ) : (
            // MULTI or EXTENSION
            <Box className="type-filter__selected">
              {visibleItems.map((item) => (
                <Tooltip key={item} title={item}>
                  <Box
                    className={
                      variant === "extension"
                        ? "type-filter__pill"
                        : "type-filter__circle"
                    }
                  >
                    {variant === "extension"
                      ? item
                      : item.charAt(0).toUpperCase()}
                  </Box>
                </Tooltip>
              ))}

              {hiddenItems.length > 0 && (
                <Tooltip title={hiddenItems.join(", ")}>
                  <Box className="type-filter__circle">
                    +{hiddenItems.length}
                  </Box>
                </Tooltip>
              )}
            </Box>
          )
        }
      >
        <Box className="type-filter__options">
          {options.length === 0 ? (
            <MenuItem disabled>No data available</MenuItem>
          ) : (
            options.map((item) => (
              <MenuItem
                key={item}
                onClick={() => handleToggle(item)}
                className="type-filter__option"
              >
                <Checkbox checked={tempValue.includes(item)} size="small" />
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{
                    className: "type-filter__text",
                  }}
                />
              </MenuItem>
            ))
          )}
        </Box>

        <Box className="type-filter__footer">
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={handleClear}
          >
            Clear
          </Button>
        </Box>
      </Select>
    </FormControl>
  );
};

export default TypeFilterDropdown;
